import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, FileText, Users, Monitor, LayoutList, Receipt, Building2, GraduationCap } from 'lucide-react';
import { Button, Pagination, StatsGrid, PageHeader } from '@/components/shared/ui';
import { FullScreenLayout, FullScreenHeader } from '@/components/shared/layout';
import OrganizationInfoModal from './modals/OrganizationInfoModal';
import AddNoteModal from './modals/AddNoteModal';
import UserList from './UserList';
import { convertApiUserToUser } from '../users/utils/userManagementUtils';
import DeviceList from './devices/DeviceList';
import { Device, Organization, Note, User } from '../../types/admin';
import { buildAdminUpdateUserPayload, adminUiRoleToApiRole } from '@/utils/adminUserUpdatePayload';
import { useAuth } from '@/contexts/AuthContext';
import AlertDialog from '@/components/shared/ui/AlertDialog';
import OrderManagement from '../order/OrderManagement';
import ClassManagement from '../class/ClassManagement';
import ClassEdit from '../class/ClassEdit';
import ClassCreate from '../class/ClassCreate';
import { formatDateTime } from '@/utils/dateUtils';
import { ROUTES } from '@/lib/constants/routes';
import Orders from '../order/Orders';
import MasterScenarioManagement from '../scenario/MasterScenarioManagement';
import ScenarioDetail from '../scenario/ScenarioDetail';
import { useDeleteDevice, useUpdateDevice } from '@/hooks/useDevice';
import { useOrganizationDeviceList } from '@/hooks/useLicense';
import { useOrganizationDetail, useCountries } from '@/hooks/useOrganization';
import { useUserListWithFilters, useRemoveUserOrganization, useUpdateUserInfo, useUpdateOrganizationUser, useBulkCreateGuestAccountsUi, useBulkCreateUsers } from '@/hooks/useUserManagement';
import {
    useOrganizationPostList,
    useCreateOrganizationPost,
    useUpdateOrganizationPost,
    useDeleteOrganizationPost,
    organizationPostKeys,
} from '@/hooks/useOrganizationPost';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/services/apiClient';
import { uploadFileViaPresignedUrl } from '@/services/fileService';
import { parseUiDeviceIdToApiOrganizationDeviceId } from '@/utils/organizationDeviceId';
import { apiPreferredKoEn } from '@/lib/i18nLocale';
import { toast } from '@/hooks/use-toast';
import { guestBulkCreateAlertFromResult } from '@/utils/guestBulkCreateAlert';
import type { CreateGuestSaveContext } from '../users/modals/CreateGuestModal';

const medicrewLogo = '/assets/images/brand/medicrew_blue_logo.png';

/** 한국어가 아닌 UI 언어에서는 기관 상세 국가 표시명을 영어로 반환 */
function getDisplayCountry(country: { id?: number; title?: string } | string | null | undefined, lang: string, t: (key: string) => string): string {
    if (!country) return '-';
    const title = typeof country === 'string' ? country : (country as any)?.title;
    if (!title) return '-';
    if (apiPreferredKoEn(lang) !== 'en') return title;
    const byId: Record<number, string> = { 1: 'organizations.countryKR' };
    const byTitle: Record<string, string> = { '대한민국': 'organizations.countryKR', '일본': 'organizations.countryJP', '미국': 'organizations.countryUS', '영국': 'organizations.countryGB', '프랑스': 'organizations.countryFR', '독일': 'organizations.countryDE' };
    const key = (typeof country === 'object' && (country as any)?.id != null) ? byId[(country as any).id] : byTitle[title];
    return key ? t(key) : title;
}

/** 한국어가 아닌 UI 언어에서는 기관 유형 표시명을 영어로 반환 */
function getDisplayOrgType(typeTitle: string | null | undefined, lang: string, t: (key: string) => string): string {
    if (!typeTitle) return '-';
    if (apiPreferredKoEn(lang) !== 'en') return typeTitle;
    const keyMap: Record<string, string> = {
        '종합병원': 'organizations.typeHospital',
        '의과대학': 'organizations.typeMedical',
        '간호대학': 'organizations.typeNursing',
        '공공기관': 'organizations.typePublic',
        '일반학교': 'organizations.typeSchool',
        '유통업체': 'organizations.typeRetail',
        '산업체': 'organizations.typeIndustry',
        '기타': 'organizations.typeOther',
    };
    const key = keyMap[typeTitle];
    return key ? t(key) : typeTitle;
}

/** OrgPage 좌측 섹션 (`?section=` 과 동기화) */
type OrgPageSidebarSection =
    | 'overview'
    | 'users'
    | 'devices'
    | 'class-management'
    | 'scenarios'
    | 'orders';

export interface OrgPageProps {
    /** 마스터/어드민 "관리자 설정" 등에서 URL 없이 사용할 때 전달. 있으면 useParams id 대신 사용 */
    overrideId?: string;
    /** 뒤로가기 경로. 미전달 시 overrideId 여부·역할에 따라 자동 결정 */
    backPath?: string;
}

export default function OrgPage(props?: OrgPageProps): React.ReactElement {
    const { overrideId, backPath: backPathProp } = props ?? {};
    const { id: paramId } = useParams<{ id: string }>();
    const id = overrideId ?? paramId;
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('admin');
    const lang = (i18n.language || '').toLowerCase().split('-')[0];
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const deleteDeviceMutation = useDeleteDevice();
    const updateDeviceMutation = useUpdateDevice();
    const isAdmin = user?.currentAccount?.role === 'admin';
    const isMaster = user?.currentAccount?.role === 'master';
    const basePath = isAdmin ? '/admin' : '/master';
    const organizationIdNum = useMemo(() => {
        if (!id) return 0;
        const n = Number(id);
        return Number.isFinite(n) && n > 0 ? n : 0;
    }, [id]);
    const createPostMutation = useCreateOrganizationPost();
    const updatePostMutation = useUpdateOrganizationPost(organizationIdNum);
    const deletePostMutation = useDeleteOrganizationPost(organizationIdNum);
    /** 마스터/어드민 "관리자 설정"에서 진입 시 뒤로가기. prop 없으면 역할·overrideId로 결정 */
    const backPath = backPathProp ?? (overrideId ? '/' : '/admin/organizations');

    const validSections: OrgPageSidebarSection[] = useMemo(
        () =>
            isMaster
                ? ['overview', 'users', 'devices', 'class-management', 'orders']
                : ['overview', 'users', 'devices', 'class-management', 'scenarios', 'orders'],
        [isMaster]
    );
    const [searchParams, setSearchParams] = useSearchParams();
    const sectionFromUrl = searchParams.get('section') as OrgPageSidebarSection | null;
    const [activeSection, setActiveSectionState] = useState<OrgPageSidebarSection>(() =>
        sectionFromUrl && validSections.includes(sectionFromUrl) ? sectionFromUrl : 'overview'
    );
    useEffect(() => {
        if (isMaster && sectionFromUrl === 'scenarios') {
            setActiveSectionState('overview');
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('section', 'overview');
                return next;
            });
            return;
        }
        if (sectionFromUrl && validSections.includes(sectionFromUrl)) {
            setActiveSectionState(sectionFromUrl);
        }
    }, [sectionFromUrl, validSections, isMaster, setSearchParams]);
    const setActiveSection = (sid: OrgPageSidebarSection) => {
        // 클래스 생성/수정 화면에서 다른 섹션으로 이동 시 경고
        const currentMode = searchParams.get('mode');
        if (currentMode === 'create' || currentMode === 'edit') {
            const confirmMessage = currentMode === 'create' 
                ? t('create.confirmCancel', { defaultValue: '저장하지 않은 변경사항이 있습니다. 페이지를 나가시겠습니까?' })
                : t('edit.confirmCancel', { defaultValue: '저장하지 않은 변경사항이 있습니다. 페이지를 나가시겠습니까?' });
            
            if (!window.confirm(confirmMessage)) {
                return; // 취소하면 이동하지 않음
            }
        }
        
        setActiveSectionState(sid);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('section', sid);
            // mode와 classId 제거 (클래스 생성/수정 상태 초기화)
            next.delete('mode');
            next.delete('classId');
            next.delete('classType');
            return next;
        });
    };

    /** 사용자·기기·클래스 탭에서만 기관 사용자 목록 필요(표·프로덕트 카드). 오버뷰 통계는 상세 집계 필드 우선 */
    const shouldFetchOrganizationUserList =
        organizationIdNum > 0 &&
        (activeSection === 'users' || activeSection === 'devices' || activeSection === 'class-management');
    const shouldFetchOrganizationDeviceList = organizationIdNum > 0 && activeSection === 'devices';
    /** 노트(포스트) 영역은 오버뷰에만 표시 */
    const shouldFetchOrganizationPosts = organizationIdNum > 0 && isAdmin && activeSection === 'overview';

    /** 사용자 목록 페이지네이션 */
    const [userListPage, setUserListPage] = useState<number>(1);
    const userListPageSize = 20;

    const {
        data: realApiData,
        isLoading: orgDetailLoading,
        error: orgDetailError,
    } = useOrganizationDetail(organizationIdNum, {
        enabled: organizationIdNum > 0,
        retry: 1,
    });

    const { data: organizationUsersData } = useUserListWithFilters(
        { 
            organizationId: organizationIdNum,
            page: userListPage, 
            pageSize: userListPageSize,
        },
        { enabled: shouldFetchOrganizationUserList, retry: 1 }
    );

    const { data: countriesData } = useCountries();

    const removeUserOrganization = useRemoveUserOrganization();
    const updateUserInfo = useUpdateUserInfo();
    const updateOrganizationUser = useUpdateOrganizationUser();
    const bulkCreateGuestAccountsUi = useBulkCreateGuestAccountsUi();
    const bulkCreateUsers = useBulkCreateUsers();

    const { data: orgDeviceData, refetch: refetchOrgDevices } = useOrganizationDeviceList(organizationIdNum, {
        enabled: shouldFetchOrganizationDeviceList,
        retry: 1,
    });

    const { data: realPostsData } = useOrganizationPostList(organizationIdNum, 1, 200, {
        enabled: shouldFetchOrganizationPosts,
        retry: false,
    });

    const [isOrgInfoModalOpen, setIsOrgInfoModalOpen] = useState<boolean>(false);
    const [orgInfoModalInitialTab, setOrgInfoModalInitialTab] = useState<'organization' | 'contact'>('organization');
    const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState<boolean>(false);
    const [editingNote, setEditingNote] = useState<Note | null>(null);

    const allSidebarSections: { id: OrgPageSidebarSection; labelKey: string; icon: typeof Building2 }[] = [
        { id: 'overview', labelKey: 'orgPage.overview', icon: Building2 },
        { id: 'users', labelKey: 'orgPage.users', icon: Users },
        { id: 'devices', labelKey: 'orgPage.devices', icon: Monitor },
        { id: 'class-management', labelKey: 'orgPage.classManagement', icon: GraduationCap },
        { id: 'scenarios', labelKey: 'orgPage.scenarios', icon: LayoutList },
        { id: 'orders', labelKey: 'orgPage.orders', icon: Receipt },
    ];
    const sidebarSections = isMaster ? allSidebarSections.filter((s) => s.id !== 'scenarios') : allSidebarSections;
    const contentTitle = sidebarSections.find((s) => s.id === activeSection) ? t(sidebarSections.find((s) => s.id === activeSection)!.labelKey) : t('orgPage.overview');
    const classFormMode = searchParams.get('mode');
    const isClassCreateOrEditMode =
        activeSection === 'class-management' &&
        (classFormMode === 'create' || classFormMode === 'edit');

    const contentActions = (() => {
        if (isClassCreateOrEditMode) return undefined;
        if (activeSection === 'class-management') {
            return (
                <div className="flex gap-2">
                    {!isMaster && (
                        <Button variant="lightdark" size="md" onClick={() => navigate(`${basePath}/organizations/${id}?section=class-management&mode=create&classType=open`)}>
                            <Plus size={16} className="mr-2" />
                            {t('orgPage.createOpenClass')}
                        </Button>
                    )}
                    <Button variant="lightdark" size="md" onClick={() => navigate(`${basePath}/organizations/${id}?section=class-management&mode=create&classType=org`)}>
                        <Plus size={16} className="mr-2" />
                        {t('orgPage.createClass')}
                    </Button>
                </div>
            );
        }
        if (activeSection === 'scenarios') {
            return null;
        }
        if (activeSection === 'orders' && isAdmin) {
            const orderCreatePath = '/admin/order-management/create';
            return (
                <Button variant="lightdark" size="md" onClick={() => navigate(orderCreatePath)}>
                    <Plus size={16} className="mr-2" />
                    {t('orgPage.createOrder')}
                </Button>
            );
        }
        return undefined;
    })();
    // Alert Dialog State
    const [alertState, setAlertState] = useState<{
        isOpen: boolean;
        type: 'success' | 'error';
        title: string;
        message: string;
    }>({
        isOpen: false,
        type: 'success',
        title: '',
        message: ''
    });

    // Pagination for notes
    const [currentNotePage, setCurrentNotePage] = useState<number>(1);
    const notesPerPage = 10;

    // 시나리오 관리: 목록 ↔ 상세 (레이아웃 유지)
    const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);

    const organization = useMemo(() => {
        if (!realApiData) return null;
        const dto = realApiData;
        const countryObj = dto.country || { id: 0, title: '' };
        const organizationTypeObj = dto.organizationType || { id: 0, title: '' };
        const statusMap: Record<string, string> = {
            ACTIVE: 'active',
            INACTIVE: 'inactive',
            EXPIRED: 'expired',
            EXPIRING_SOON: 'expiring',
        };
        return {
            id: String(dto.organizationId),
            name: dto.title || '',
            logo: dto.logoImageUrl || '',
            country: countryObj,
            type: organizationTypeObj.title || '',
            organizationType: organizationTypeObj,
            countryCallingCode: dto.countryCallingCode || { id: 0, code: '', title: '' },
            businessNumber: dto.businessRegistrationNumber || '',
            contactPerson: dto.managerName || '',
            department: dto.department || '',
            position: '',
            phone: dto.phoneNumber || '',
            email: dto.email || '',
            registeredDate: dto.createdAt || '',
            status: dto.isActive ? 'active' : 'inactive',
            licenseType: '사용자구독',
            licenseCount: 0,
            deviceCount: dto.deviceCount,
            userCount: dto.userCount,
            expiryDate: '',
            isPartner: dto.isPartner ?? false,
        } as any;
    }, [realApiData]);

    const orgUsers = useMemo(
        () => (organizationUsersData?.userList ?? []).map((dto) => 
            convertApiUserToUser(dto, {
                apiCountryList: countriesData?.countryList,
                locale: lang,
            })
        ),
        [organizationUsersData?.userList, countriesData?.countryList, lang]
    );

    const orgDevices: Device[] = useMemo(() => {
        const list = orgDeviceData?.devices ?? [];
        return list.map((d) => ({
            id: String(d.id),
            deviceName: d.title,
            name: d.title,
            modelName: d.model,
            purchaseSource: d.purchaseSource ?? '',
            registeredDate: d.createdAt ? d.createdAt.slice(0, 10) : '',
            lastConnection: d.lastSigninAt ? d.lastSigninAt.slice(0, 10) : '',
            status: (d.isActive ? 'active' : 'inactive') as 'active' | 'inactive',
        }));
    }, [orgDeviceData?.devices]);

    const notesList: Note[] = useMemo(() => {
        if (!isAdmin) return [];
        const list = realPostsData?.postList ?? [];
        return list.map((post) => {
            const fileUrls = post.fileUrls.filter(Boolean);
            return {
                id: String(post.organizationPostId),
                adminId: '',
                adminName: '관리자',
                createdAt: post.createdAt,
                content: post.content,
                links: fileUrls,
                attachments: fileUrls.map((u) => (u ? u.split('/').filter(Boolean).pop() : '') || 'file'),
                images: [],
            };
        });
    }, [isAdmin, realPostsData]);

    // Pagination logic for notes (before early return to ensure hooks are called)
    const notesTotalPages = Math.ceil(notesList.length / notesPerPage);
    const notesStartIndex = (currentNotePage - 1) * notesPerPage;
    const notesEndIndex = notesStartIndex + notesPerPage;
    const paginatedNotes = useMemo(() => {
        return notesList.slice(notesStartIndex, notesEndIndex);
    }, [notesList, notesStartIndex, notesEndIndex]);

    const overviewStats = useMemo(
        () => ({
            userCount: realApiData?.userCount ?? organizationUsersData?.totalCount ?? organizationUsersData?.userList?.length ?? 0,
            orderCount: realApiData?.orderCount ?? 0,
            deviceCount: realApiData?.deviceCount ?? orgDeviceData?.devices?.length ?? 0,
            scenarioCount: realApiData?.classCount ?? 0,
        }),
        [realApiData, organizationUsersData, orgDeviceData?.devices?.length]
    );

    useEffect(() => {
        if (activeSection === 'users') {
            setUserListPage(1);
        }
    }, [activeSection]);

    if (!id || organizationIdNum <= 0) {
        return (
            <FullScreenLayout
                backgroundColor="bg-white"
                header={
                    <FullScreenHeader title={t('orgInfo')} onBack={() => navigate(-1)} />
                }
            >
                <div className="max-w-[1600px] px-5 py-6 mx-auto">
                    <p className="text-gray-600">{t('orgPage.notFound')}</p>
                </div>
            </FullScreenLayout>
        );
    }

    if (orgDetailLoading) {
        return (
            <FullScreenLayout
                backgroundColor="bg-white"
                header={
                    <FullScreenHeader title={t('orgInfo')} onBack={() => navigate(-1)} />
                }
            >
                <div className="max-w-[1600px] px-5 py-6 mx-auto">
                    <p className="text-gray-600">{t('orgPage.loading', { defaultValue: '로딩 중...' })}</p>
                </div>
            </FullScreenLayout>
        );
    }

    if (orgDetailError) {
        const isApi = orgDetailError instanceof ApiError;
        const api = isApi ? (orgDetailError as ApiError) : null;
        return (
            <FullScreenLayout
                backgroundColor="bg-white"
                header={
                    <FullScreenHeader title={t('orgInfo')} onBack={() => navigate(-1)} />
                }
            >
                <div className="max-w-[1600px] px-5 py-6 mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h3 className="text-red-900 font-semibold mb-2">{t('orgPage.apiError', { defaultValue: 'API 오류' })}</h3>
                        <p className="text-red-700 text-sm">{(orgDetailError as Error)?.message || t('orgPage.apiLoadError', { defaultValue: '기관 정보를 불러오지 못했습니다.' })}</p>
                        {api?.status != null && (
                            <p className="text-red-600 text-xs mt-2">
                                {t('orgPage.apiStatus', { defaultValue: 'HTTP' })} {api.status} {api.statusText || ''}
                            </p>
                        )}
                    </div>
                </div>
            </FullScreenLayout>
        );
    }

    if (!organization) {
        return (
            <FullScreenLayout
                backgroundColor="bg-white"
                header={
                    <FullScreenHeader title={t('orgInfo')} onBack={() => navigate(-1)} />
                }
            >
                <div className="max-w-[1600px] px-5 py-6 mx-auto">
                    <p className="text-gray-600">{t('orgPage.notFound')}</p>
                </div>
            </FullScreenLayout>
        );
    }

    // Handlers
    const handleSaveOrganization = (_updatedOrg: Organization) => {
        setAlertState({
            isOpen: true,
            type: 'success',
            title: t('orgPage.orgSavedTitle'),
            message: t('orgPage.orgSavedMessage'),
        });
    };

    const handleDeviceDelete = (deviceId: string) => {
        const orgDeviceId = parseUiDeviceIdToApiOrganizationDeviceId(deviceId);
        if (orgDeviceId == null) {
            setAlertState({
                isOpen: true,
                type: 'error',
                title: t('orgPage.apiError'),
                message: t('orgPage.deviceDeleteInvalidId'),
            });
            return;
        }
        deleteDeviceMutation.mutate(orgDeviceId, {
            onSuccess: () => {
                void refetchOrgDevices();
                setAlertState({
                    isOpen: true,
                    type: 'success',
                    title: t('orgPage.deviceDeleteSuccessTitle'),
                    message: t('orgPage.deviceDeleteSuccessMessage'),
                });
            },
            onError: (err: Error) => {
                setAlertState({
                    isOpen: true,
                    type: 'error',
                    title: t('orgPage.deviceDeleteErrorTitle'),
                    message: err?.message || t('orgPage.deviceDeleteErrorMessage'),
                });
            },
        });
    };

    const handleDeviceUpdate = (updatedDevice: Device): Promise<void> => {
        return new Promise((resolve, reject) => {
            const orgDeviceId = parseUiDeviceIdToApiOrganizationDeviceId(String(updatedDevice.id));
            if (orgDeviceId == null) {
                setAlertState({
                    isOpen: true,
                    type: 'error',
                    title: t('orgPage.apiError'),
                    message: t('orgPage.deviceDeleteInvalidId'),
                });
                reject(new Error('invalid id'));
                return;
            }
            const orig = orgDevices.find((d) => String(d.id) === String(updatedDevice.id));
            const title = (updatedDevice.deviceName ?? updatedDevice.name ?? '').trim();
            const purchase = (updatedDevice.purchaseSource ?? '').trim();
            const active = updatedDevice.status === 'active';
            if (!title) {
                setAlertState({
                    isOpen: true,
                    type: 'error',
                    title: t('orgPage.deviceUpdateErrorTitle'),
                    message: t('orgPage.deviceUpdateTitleRequired'),
                });
                reject(new Error('title required'));
                return;
            }
            if (orig) {
                const t0 = (orig.deviceName ?? orig.name ?? '').trim();
                const p0 = (orig.purchaseSource ?? '').trim();
                if (t0 === title && p0 === purchase && (orig.status === 'active') === active) {
                    resolve();
                    return;
                }
            }
            updateDeviceMutation.mutate(
                {
                    organizationDeviceId: orgDeviceId,
                    data: { title, purchaseSource: purchase || undefined, isActive: active },
                },
                {
                    onSuccess: () => {
                        void refetchOrgDevices();
                        setAlertState({
                            isOpen: true,
                            type: 'success',
                            title: t('orgPage.deviceUpdateSuccessTitle'),
                            message: t('orgPage.deviceUpdateSuccessMessage'),
                        });
                        resolve();
                    },
                    onError: (err) => {
                        const message = err instanceof Error ? err.message : t('orgPage.deviceUpdateErrorMessage');
                        setAlertState({
                            isOpen: true,
                            type: 'error',
                            title: t('orgPage.deviceUpdateErrorTitle'),
                            message: message || t('orgPage.deviceUpdateErrorMessage'),
                        });
                        reject(err instanceof Error ? err : new Error('update failed'));
                    },
                }
            );
        });
    };

    const handleAddNote = async (note: Note & { attachmentFiles?: File[]; retainedLinkUrls?: string[] }) => {
        if (organizationIdNum <= 0) throw new Error('invalid org');
        const contentTrim = (note.content || '').trim();
        if (!contentTrim) {
            setAlertState({ isOpen: true, type: 'error', title: t('orgPage.apiError'), message: t('orgPage.noteContentRequired', { defaultValue: '노트 내용을 입력해주세요.' }) });
            throw new Error('empty content');
        }
        const isEdit = Boolean(editingNote && note.id);
        const retained = (note.retainedLinkUrls ?? (editingNote?.links as string[] | undefined) ?? []).map((u) => String(u).trim()).filter(Boolean);
        const uploadedUrls: string[] = [];
        try {
            for (const file of note.attachmentFiles ?? []) {
                uploadedUrls.push(await uploadFileViaPresignedUrl(file, 'EDU'));
            }
        } catch (uploadErr: unknown) {
            const msg = uploadErr instanceof Error ? uploadErr.message : t('orgPage.attachmentUploadFailed', { defaultValue: '첨부 업로드에 실패했습니다.' });
            setAlertState({ isOpen: true, type: 'error', title: t('orgPage.attachmentUploadFailed', { defaultValue: '첨부 업로드 실패' }), message: msg });
            throw uploadErr instanceof Error ? uploadErr : new Error(msg);
        }
        const fileUrls = [...retained, ...uploadedUrls].filter(Boolean);
        try {
            if (isEdit && note.id) {
                await updatePostMutation.mutateAsync({
                    organizationPostId: Number(note.id),
                    data: { content: contentTrim, fileUrls },
                });
            } else {
                await createPostMutation.mutateAsync({
                    organizationId: organizationIdNum,
                    content: contentTrim,
                    ...(fileUrls.length > 0 ? { fileUrls } : {}),
                });
            }
            await queryClient.invalidateQueries({
                predicate: (q) =>
                    Array.isArray(q.queryKey) && q.queryKey[0] === organizationPostKeys.all[0],
            });
            setCurrentNotePage(1);
            setIsAddNoteModalOpen(false);
            setEditingNote(null);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : t('orgPage.noteSaveError', { defaultValue: '노트 저장 중 오류가 발생했습니다.' });
            setAlertState({ isOpen: true, type: 'error', title: t('orgPage.noteSaveErrorTitle', { defaultValue: '노트 저장 실패' }), message: msg });
            throw e instanceof Error ? e : new Error(msg);
        }
    };

    const handleEditNote = (note: Note) => {
        setEditingNote(note);
        setIsAddNoteModalOpen(true);
    };

    const handleDeleteNote = async (noteId: string) => {
        if (!window.confirm('이 노트를 삭제하시겠습니까?')) return;
        try {
            await deletePostMutation.mutateAsync(Number(noteId));
            await queryClient.invalidateQueries({
                predicate: (q) =>
                    Array.isArray(q.queryKey) && q.queryKey[0] === organizationPostKeys.all[0],
            });
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : '노트 삭제 중 오류가 발생했습니다.';
            setAlertState({ isOpen: true, type: 'error', title: '노트 삭제 실패', message: msg });
        }
    };

    const handleDeleteSelectedUsers = async (users: { id: string | number }[]) => {
        try {
            const userIdList = users.map(u => Number(u.id));
            await removeUserOrganization.mutateAsync({ userIdList });
            setAlertState({ 
                isOpen: true, 
                type: 'success', 
                title: '사용자 삭제 완료', 
                message: `${users.length}명의 사용자를 기관에서 내보냈습니다.` 
            });
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : '사용자 삭제 중 오류가 발생했습니다.';
            setAlertState({ isOpen: true, type: 'error', title: '사용자 삭제 실패', message: msg });
        }
    };

    const handleSaveUser = async (updatedUser: User) => {
        const prevUser = orgUsers.find((user) => String(user.id) === String(updatedUser.id));
        if (!prevUser) {
            setAlertState({
                isOpen: true,
                type: 'error',
                title: '사용자 정보 수정 실패',
                message: '수정할 사용자를 찾을 수 없습니다.',
            });
            throw new Error('user not found');
        }

        console.log('🔍 [OrgPage] handleSaveUser - prevUser.role:', prevUser.role);
        console.log('🔍 [OrgPage] handleSaveUser - updatedUser.role:', updatedUser.role);
        console.log('🔍 [OrgPage] handleSaveUser - prevUser:', prevUser);
        console.log('🔍 [OrgPage] handleSaveUser - updatedUser:', updatedUser);

        const built = buildAdminUpdateUserPayload(prevUser, updatedUser);
        console.log('🔍 [OrgPage] handleSaveUser - built:', built);
        if (built.type === 'noop') {
            if (built.reason === 'invalid_id') {
                setAlertState({
                    isOpen: true,
                    type: 'error',
                    title: '사용자 정보 수정 실패',
                    message: '유효하지 않은 사용자 ID입니다.',
                });
                throw new Error('invalid user id');
            }
            setAlertState({
                isOpen: true,
                type: 'success',
                title: '변경 사항 없음',
                message: '변경된 내용이 없습니다.',
            });
            return;
        }

        try {
            if (isMaster) {
                // 마스터는 기관 유저 정보 수정 API 사용 (소속, 직책, 직무, 핸드폰번호, 권한 수정 가능)
                const orgUserPayload = {
                    userId: built.payload.userId,
                    ...(built.payload.affiliation !== undefined && { affiliation: built.payload.affiliation }),
                    ...(built.payload.position !== undefined && { position: built.payload.position }),
                    ...(built.payload.jobTitle !== undefined && { jobTitle: built.payload.jobTitle }),
                    ...(built.payload.customJobTitle !== undefined && { customJobTitle: built.payload.customJobTitle }),
                    ...(built.payload.phoneNumber !== undefined && { phoneNumber: built.payload.phoneNumber }),
                    ...(built.payload.role !== undefined && { role: built.payload.role }),
                };
                console.log('🔍 [OrgPage] orgUserPayload:', orgUserPayload);
                await updateOrganizationUser.mutateAsync(orgUserPayload);
            } else {
                // 어드민은 기존 API 사용
                await updateUserInfo.mutateAsync(built.payload);
            }
            setAlertState({
                isOpen: true,
                type: 'success',
                title: '사용자 정보 수정 완료',
                message: '사용자 정보가 수정되었습니다.',
            });
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : '사용자 정보 수정 중 오류가 발생했습니다.';
            setAlertState({ isOpen: true, type: 'error', title: '사용자 정보 수정 실패', message: msg });
            throw e;
        }
    };

    const handleCreateGuest = async (newGuests: Partial<User>[], context: CreateGuestSaveContext) => {
        if (!organizationIdNum || organizationIdNum <= 0) {
            toast({
                title: '오류',
                description: '기관 ID가 유효하지 않습니다.',
                variant: 'destructive',
            });
            return;
        }

        try {
            // 자동 생성 탭 (idPrefix 존재)
            if (context.idPrefix) {
                const response = await bulkCreateGuestAccountsUi.mutateAsync({
                    organizationId: organizationIdNum,
                    idPrefix: context.idPrefix,
                    namePrefix: context.namePrefix || '',
                    password: context.password || '',
                    count: newGuests.length,
                    validityDays: context.validityDays,
                    language: apiPreferredKoEn(i18n.language),
                });

                const alert = guestBulkCreateAlertFromResult(response);
                toast({
                    title: alert.title,
                    description: alert.message,
                    variant: alert.type === 'success' ? 'default' : 'destructive',
                });
            } 
            // 파일 업로드 탭 (idPrefix 없음)
            else {
                if (!context.uploadedFile) {
                    toast({
                        title: '오류',
                        description: '업로드된 파일이 없습니다.',
                        variant: 'destructive',
                    });
                    return;
                }

                const response = await bulkCreateUsers.mutateAsync({
                    organizationId: organizationIdNum,
                    file: context.uploadedFile,
                    validityDays: context.validityDays,
                    language: apiPreferredKoEn(i18n.language),
                });

                const alert = guestBulkCreateAlertFromResult(response);
                toast({
                    title: alert.title,
                    description: alert.message,
                    variant: alert.type === 'success' ? 'default' : 'destructive',
                });
            }
        } catch (error) {
            console.error('게스트 생성 실패:', error);
            const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
            toast({
                title: '게스트 생성 실패',
                description: errorMessage,
                variant: 'destructive',
            });
        }
    };

    const openOrgInfoModal = () => {
        setOrgInfoModalInitialTab('organization');
        setIsOrgInfoModalOpen(true);
    };
    const openOrgContactModal = () => {
        setOrgInfoModalInitialTab('contact');
        setIsOrgInfoModalOpen(true);
    };

    return (
        <>
        <FullScreenLayout
            backgroundColor="bg-white"
            header={
                <FullScreenHeader
                    title={organization.name}
                    onBack={undefined}
                    onHome={isMaster ? () => navigate(ROUTES.HOME) : undefined}
                    homeLogo={isMaster ? medicrewLogo : undefined}
                    homeLabel={t('home')}
                    logo={organization.logo || undefined}
                    logoFallbackText={organization.name || t('orgPage.logoFallback')}
                    rightContent={isAdmin ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(ROUTES.ADMIN.ORGANIZATIONS)}
                        >
                            {t('orgPage.exit')}
                        </Button>
                    ) : undefined}
                />
            }
        >
            <div className="flex flex-1 min-h-0">
                {/* 좌측 사이드 메뉴 */}
                <nav className="w-56 bg-white border-r border-gray-200 flex-shrink-0 flex flex-col">
                    <div className="flex-1 py-2">
                        {sidebarSections.map(({ id, labelKey, icon: Icon }) => {
                            const isActive = activeSection === id;
                            const handleClick = () => setActiveSection(id);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={handleClick}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors relative ${isActive ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                                >
                                    {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-600 rounded-r" />}
                                    <Icon size={18} className={`flex-shrink-0 ${isActive ? 'text-brand-600' : 'text-gray-500'}`} />
                                    {t(labelKey)}
                                </button>
                            );
                        })}
                    </div>
                </nav>

                {/* 기관정보 */}
                <div className="flex-1 min-w-0 overflow-auto">
                    <div className="max-w-[1600px] p-8 mx-auto">
                        {!isClassCreateOrEditMode && (
                            <PageHeader title={contentTitle} titleSize="admin" actions={contentActions} />
                        )}
                        {activeSection === 'overview' && (
                            <>
                                {/* 통계 카드: 사용자수, 주문건수, 기기수, 클래스 수 — GET /organization/:id/detail */}
                                <StatsGrid
                                    columns={4}
                                    items={[
                                        { label: t('orgPage.userCount'), value: overviewStats.userCount, sub: 'USERS', icon: Users, color: 'brand' },
                                        { label: t('orgPage.orderCount'), value: overviewStats.orderCount, sub: 'ORDERS', icon: Receipt, color: 'brand' },
                                        { label: t('orgPage.deviceCount'), value: overviewStats.deviceCount, sub: 'DEVICES', icon: Monitor, color: 'brand' },
                                        { label: t('orgPage.scenarioCount'), value: overviewStats.scenarioCount, sub: 'CLASSES', icon: GraduationCap, color: 'brand' },
                                    ]}
                                />

                                {/* 기관정보 카드(좌) / 담당자 정보 카드(우) */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                                            <h3 className="text-sm font-semibold text-gray-700">{t('orgPage.orgInfoCard')}</h3>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openOrgInfoModal();
                                                }}
                                                className="text-sm font-medium text-brand-600 hover:text-brand-700 cursor-pointer"
                                            >
                                                {t('orgPage.edit')}
                                            </button>
                                        </div>
                                        <div className="p-5 space-y-4">
                                            <div>
                                                <span className="text-xs font-medium text-gray-400 block mb-1">{t('orgPage.countryType')}</span>
                                                <p className="text-gray-800">
                                                    {getDisplayCountry(organization.country, lang, t)} / {getDisplayOrgType(organization.type, lang, t)}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-xs font-medium text-gray-400 block mb-1">{t('orgPage.businessNumber')}</span>
                                                <p className="text-gray-800">{organization.businessNumber || '-'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs font-medium text-gray-400 block mb-1">{t('orgPage.registeredDate')}</span>
                                                <p className="text-gray-800">{formatDateTime(organization.registeredDate, i18n.language)}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                                            <h3 className="text-sm font-semibold text-gray-700">{t('orgPage.contactCard')}</h3>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openOrgContactModal();
                                                }}
                                                className="text-sm font-medium text-brand-600 hover:text-brand-700 cursor-pointer"
                                            >
                                                {t('orgPage.edit')}
                                            </button>
                                        </div>
                                        <div className="p-5 space-y-4">
                                            <div>
                                                <span className="text-xs font-medium text-gray-400 block mb-1">{t('orgPage.contactPerson')}</span>
                                                <p className="text-gray-800">{organization.contactPerson || '-'} {organization.department ? ` ${organization.department}` : ''} {organization.position ? ` ${organization.position}` : ''}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs font-medium text-gray-400 block mb-1">{t('orgPage.contact')}</span>
                                                <p className="text-gray-800">{organization.phone || '-'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs font-medium text-gray-400 block mb-1">{t('orgPage.email')}</span>
                                                <p className="text-gray-800">{organization.email || '-'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 노트 (하단) - 어드민 관리자만 조회 및 작성 가능 */}
                                {isAdmin && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm mt-8">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h2 className="text-lg font-semibold text-gray-900">{t('orgPage.notes')}</h2>
                        <Button
                            variant="lightdark"
                            size="md"
                            onClick={() => {
                                setEditingNote(null);
                                setIsAddNoteModalOpen(true);
                            }}
                        >
                            <Plus size={16} className="mr-2" />
                            {t('orgPage.addNote')}
                        </Button>
                    </div>

                    <div className="p-6">
                        {paginatedNotes.length > 0 ? (
                            <div className="space-y-6">
                                {paginatedNotes.map((note) => (
                                    <div key={note.id} className="border-b border-gray-100 pb-6 last:border-b-0 last:pb-0">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <span className="font-semibold text-gray-900">{note.adminName}</span>
                                                <span className="text-sm text-gray-500">{formatDateTime(note.createdAt, i18n.language)}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEditNote(note)}
                                                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                                >
                                                    수정
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteNote(note.id)}
                                                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                                                >
                                                    삭제
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-gray-700 whitespace-pre-wrap mb-4 leading-relaxed">{note.content}</p>

                                        {/* Attachments */}
                                        {note.attachments && note.attachments.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {note.attachments.map((attachment, idx) => {
                                                    const fileUrl = note.links?.[idx];
                                                    const handleDownload = async (e: React.MouseEvent) => {
                                                        e.preventDefault();
                                                        if (!fileUrl) {
                                                            alert('파일 URL이 없습니다.');
                                                            return;
                                                        }
                                                        try {
                                                            const response = await fetch(fileUrl);
                                                            const blob = await response.blob();
                                                            const url = window.URL.createObjectURL(blob);
                                                            const a = document.createElement('a');
                                                            a.href = url;
                                                            a.download = attachment;
                                                            document.body.appendChild(a);
                                                            a.click();
                                                            document.body.removeChild(a);
                                                            window.URL.revokeObjectURL(url);
                                                        } catch (error) {
                                                            console.error('파일 다운로드 실패:', error);
                                                            alert('파일 다운로드에 실패했습니다.');
                                                        }
                                                    };
                                                    
                                                    return (
                                                        <button
                                                            key={idx}
                                                            type="button"
                                                            onClick={handleDownload}
                                                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-sm text-gray-700 transition-colors cursor-pointer"
                                                        >
                                                            <FileText size={14} className="text-gray-500" />
                                                            {attachment}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <FileText size={48} className="mx-auto mb-4 text-gray-300" />
                                <p className="text-gray-500 text-sm">{t('orgPage.noNotes')}</p>
                            </div>
                        )}
                    </div>

                    {/* Pagination for notes */}
                    {notesList.length > 0 && (
                        <div className="px-6 pb-4">
                            <Pagination
                                currentPage={currentNotePage}
                                totalPages={notesTotalPages}
                                totalItems={notesList.length}
                                itemsPerPage={notesPerPage}
                                onPageChange={setCurrentNotePage}
                            />
                        </div>
                    )}
                </div>
                                )}
                            </>
                        )}

                        {/* 사용자 관리 */}
                        {activeSection === 'users' && id && (
                            <UserList
                                users={orgUsers}
                                organizationId={Number(id)}
                                organizationName={organization?.name}
                                omitLicenseQuotaCheck
                                currentPage={userListPage}
                                totalPages={organizationUsersData?.totalCount ? Math.ceil(organizationUsersData.totalCount / userListPageSize) : 1}
                                totalCount={organizationUsersData?.totalCount}
                                onPageChange={setUserListPage}
                                masterUserCount={organizationUsersData?.masterUserCount}
                                studentUserCount={organizationUsersData?.studentUserCount}
                                onDeleteSelectedUsers={handleDeleteSelectedUsers}
                                onSaveUser={handleSaveUser}
                                onCreateGuest={handleCreateGuest}
                            />
                        )}
                        {/* 기기 관리 */}
                        {activeSection === 'devices' && (
                            <DeviceList
                                devices={orgDevices}
                                organizationId={id && !Number.isNaN(Number(id)) ? Number(id) : undefined}
                                organizationName={organization?.name}
                                showStats={true}
                                onDeviceUpdate={handleDeviceUpdate}
                                onDeviceDelete={handleDeviceDelete}
                            />
                        )}
                        {/* 클래스 관리 */}
                        {activeSection === 'class-management' && (() => {
                            const classIdParam = searchParams.get('classId');
                            const mode = searchParams.get('mode');
                            
                            // 클래스 수정 모드
                            if (classIdParam && mode === 'edit') {
                                const classIdNum = Number(classIdParam);
                                const returnPath = `${basePath}/organizations/${id}?section=class-management`;
                                return <ClassEdit classIdProp={classIdNum} returnPath={returnPath} />;
                            }
                            
                            // 클래스 생성 모드
                            if (mode === 'create') {
                                const classType = searchParams.get('classType') === 'open' ? 'open' : 'org';
                                const returnPath = `${basePath}/organizations/${id}?section=class-management`;
                                return (
                                    <ClassCreate
                                        returnPath={returnPath}
                                        defaultOrganizationId={classType === 'org' ? organizationIdNum : undefined}
                                        classType={classType}
                                    />
                                );
                            }
                            
                            // 클래스 목록
                            return (
                                <ClassManagement
                                    organizationId={id}
                                    organizationName={organization?.name}
                                    embedMode
                                />
                            );
                        })()}
                        {/* 시나리오 관리 (어드민만 — 마스터는 메뉴 비노출) */}
                        {activeSection === 'scenarios' && !isMaster && (
                            <div>
                                {selectedScenarioId ? (
                                    <>
                                        <div className="mb-4">
                                            <Button variant="ghost" size="sm" onClick={() => setSelectedScenarioId(null)} className="text-gray-600 hover:text-gray-900">
                                                ← {t('orgPage.backToList')}
                                            </Button>
                                        </div>
                                        <ScenarioDetail scenarioId={selectedScenarioId} hideBreadcrumbs />
                                    </>
                                ) : (
                                    <MasterScenarioManagement embedMode onScenarioClick={(sid) => setSelectedScenarioId(String(sid))} />
                                )}
                            </div>
                        )}
                        {/* 주문 내역 */}
                        {activeSection === 'orders' && (
                            <div>
                                {isAdmin ? (
                                    <OrderManagement
                                        embedMode
                                        organizationName={organization?.name}
                                        organizationId={id ? Number(id) : undefined}
                                    />
                                ) : (
                                    <Orders organizationId={id} embedMode />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </FullScreenLayout>

            <OrganizationInfoModal
                isOpen={isOrgInfoModalOpen}
                onClose={() => setIsOrgInfoModalOpen(false)}
                organization={organization}
                initialTab={orgInfoModalInitialTab}
            />
            {isAdmin && (
                <AddNoteModal
                    isOpen={isAddNoteModalOpen}
                    onClose={() => {
                        setIsAddNoteModalOpen(false);
                        setEditingNote(null);
                    }}
                    onSave={handleAddNote}
                    editNote={editingNote}
                />
            )}
            <AlertDialog
                isOpen={alertState.isOpen}
                onClose={() => setAlertState(prev => ({ ...prev, isOpen: false }))}
                title={alertState.title}
                message={alertState.message}
            />
        </>
    );
}
